/**
 * Rótulo pt-BR do tipo de andamento arbitral. Mora em `shared/` porque as DUAS telas o usam: a
 * empresa (`arbitral-detalhes`) já tinha o mapa, e a Secretaria caía em `rotularEnum`, que só
 * troca `_` por espaço e devolve "citacao eletronica enviada" — sem acento e em caixa baixa
 * (item 12 — 86ak89ud5). Um mapa só evita a quinta cópia (ver TODO(#794)).
 */
const ROTULO_TIPO_ANDAMENTO: Record<string, string> = {
  CRIACAO: 'Criação do processo',
  STATUS_ALTERADO: 'Status alterado',
  DOCUMENTO_ANEXADO: 'Documento anexado',
  MENSAGEM_ENVIADA: 'Mensagem enviada',
  PENDENCIA_CRIADA: 'Pendência criada',
  PENDENCIA_RESOLVIDA: 'Pendência resolvida',
  FASE_CONCLUIDA: 'Fase concluída',
  NOTIFICACAO_ENVIADA: 'Notificação enviada',
  PARTE_ADICIONADA: 'Parte adicionada',
  DECISAO: 'Decisão',
  // `MANUAL` é a marca técnica de origem de todo andamento nascido de um bloco de fluxo
  // (executor genérico) — não é um método de comprovação. "Registro manual" no selo confundia
  // com "citação comprovada manualmente" (mesma armadilha corrigida no selo da Secretaria,
  // AndamentoTimelineItem.tsx, ClickUp 86ak5xmk5). O método real já está no texto do andamento.
  MANUAL: 'Andamento',
  TRIAGEM_INICIADA: 'Triagem iniciada',
  TRIAGEM_APROVADA: 'Triagem aprovada',
  TRIAGEM_NEGADA: 'Triagem negada',
  INTIMACAO_DOCUMENTO_FALTANTE: 'Intimação de documento faltante',
  DOCUMENTO_RECEBIDO: 'Documento recebido',
  CITACAO_ELETRONICA_ENVIADA: 'Disparo de citação eletrônica',
  AR_ENVIADO: 'AR enviado',
  AR_RETORNADO: 'AR retornado',
  // Os seis do fluxo em que o requerente autoriza e custeia a carta. A redação bate
  // LITERALMENTE com `vocabularioAndamentoPortal.ts` do core — um teste de paridade
  // prende as duas listas, porque rótulo divergente entre o portal e a timeline faz o
  // mesmo ato parecer dois.
  AR_AUTORIZADO: 'Envio de carta autorizado',
  AR_RECUSADO: 'Envio de carta recusado',
  AR_RECUSA_DESFEITA: 'Recusa do envio desfeita',
  AR_ENDERECO_DEVOLVIDO: 'Endereço devolvido para correção',
  AR_PRAZO_VENCIDO: 'Prazo para autorizar o envio venceu',
  AR_SOLICITACAO_CANCELADA: 'Solicitação de envio cancelada',
  MANIFESTACAO_REGISTRADA: 'Manifestação registrada',
  MANIFESTACAO_DESCONSIDERADA: 'Manifestação desconsiderada',
  CONCLUSO_FEITO: 'Concluso feito',
  DECURSO_PUBLICADO: 'Decurso de prazo publicado',
  SORTEIO_ARBITRO: 'Sorteio de árbitro',
  TERMO_IMPARCIALIDADE_JUNTADO: 'Termo de imparcialidade juntado',
  TERMO_ARBITRAGEM_PUBLICADO: 'Termo de arbitragem publicado',
  SENTENCA_PUBLICADA: 'Sentença publicada',
  INTIMACAO_SENTENCA_ENVIADA: 'Intimação de sentença enviada',
  EMBARGOS_RECEBIDOS: 'Embargos recebidos',
  TRANSITO_PUBLICADO: 'Trânsito em julgado publicado',
  PAGAMENTO_CONFIRMADO: 'Pagamento confirmado',
  PAGAMENTO_VENCIDO: 'Pagamento vencido',
  OFICIO_TERCEIRO_ENVIADO: 'Ofício a terceiro enviado',
  OFICIO_TERCEIRO_RESPONDIDO: 'Ofício a terceiro respondido',
  KYC_SOLICITADO: 'KYC solicitado',
  KYC_VALIDADO: 'KYC validado',
  KYC_RECUSADO: 'KYC recusado',
  ALTERACAO_DADO_CADASTRO: 'Alteração de dado cadastral',
  BLOQUEIO_OVERRIDE_REGISTRADO: 'Bloqueio/override registrado',
  // Antes caíam no fallback genérico (`cru`) e perdiam o acento — "Desistencia registrada",
  // "Pagamento estornado" sem "a" final por coincidência. Fechando o mapa evita a mesma paridade
  // quebrar de novo no portal do notificado, que copia este vocabulário (ClickUp 86akae891).
  DESISTENCIA_REGISTRADA: 'Desistência registrada',
  PAGAMENTO_ESTORNADO: 'Pagamento estornado',
  // A devolução da triagem e a resposta que espera validação gravavam "Triagem iniciada" e
  // "Pendência resolvida" (QA de 01/10/2026). Mesma redação do `vocabularioAndamentoPortal.ts`.
  TRIAGEM_DEVOLVIDA: 'Triagem devolvida para correção',
  PENDENCIA_RESPONDIDA: 'Pendência respondida',
  // C2/C3: mesmo título do core (`rotulosAndamentoArbitral.ts` e o portal da parte).
  NOMEACAO_RECUSADA: 'Nomeação recusada',
};

export function rotuloTipoAndamento(tipo: string): string {
  // `Object.hasOwn`: `ROTULO_TIPO_ANDAMENTO['toString']` acharia a função herdada do protótipo
  // e o selo tentaria renderizar uma função.
  if (Object.hasOwn(ROTULO_TIPO_ANDAMENTO, tipo)) return ROTULO_TIPO_ANDAMENTO[tipo];
  // Tipo novo no banco, ainda fora do mapa: rótulo genérico legível em vez do enum cru —
  // "SENTENCA_PUBLICADA" → "Sentenca publicada". 'Andamento' fica só para entrada vazia,
  // senão todo tipo novo ficaria indistinguível do rótulo de MANUAL.
  const cru = tipo.replace(/_/g, ' ').toLowerCase().trim();
  return cru ? cru.charAt(0).toUpperCase() + cru.slice(1) : 'Andamento';
}
